/** API base for translator handoff redeem (must end with /api). */
export function getVideoApiBase(): string {
  const fromEnv = (process.env.REACT_APP_API_BASE || '').trim();
  if (fromEnv) {
    return fromEnv.replace(/\/$/, '');
  }
  return 'https://api.estecharat.com/api';
}

/** Patient/doctor/admin links from {@link VideoJoinHandoffService} (admin video monitor, portal handoff). */
export async function redeemVideoJoinHandoff(handoffId: string): Promise<string> {
  const apiBase = getVideoApiBase();
  const resp = await fetch(`${apiBase}/appointment/videoJoinHandoff/redeem`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ handoffId }),
  });
  if (!resp.ok) {
    throw new Error(await readRedeemError(resp));
  }
  const json = await resp.json();
  const token = json?.token || json?.accessToken || '';
  if (!token) {
    throw new Error('Video server did not return a join token.');
  }
  return token;
}

export async function redeemTranslatorHandoff(handoffId: string): Promise<string> {
  const apiBase = getVideoApiBase();
  const resp = await fetch(`${apiBase}/appointment/translatorJoinHandoff/redeem`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ handoffId }),
  });
  if (!resp.ok) {
    throw new Error(await readRedeemError(resp));
  }
  const json = await resp.json();
  const token = json?.token || json?.accessToken || '';
  if (!token) {
    throw new Error('Video server did not return a join token.');
  }
  return token;
}

async function readRedeemError(resp: Response): Promise<string> {
  let message = 'Invite link is not active yet.';
  try {
    const body = await resp.json();
    message = body?.message || body?.error || message;
  } catch {
    const txt = await resp.text().catch(() => '');
    if (txt) {
      message = txt;
    }
  }
  return message;
}

function isTranslatorInviteUrl(): boolean {
  const params = new URLSearchParams(window.location.search);
  if (params.get('inv') === 'translator') {
    return true;
  }
  // Legacy translator emails: hid + room/name, no admin JWT or region param.
  return Boolean(params.get('hid')) && !params.get('t') && !params.get('token') && !params.get('region');
}

/** Redeem translator invite or admin/portal video handoff. */
export async function redeemHandoffFromUrl(handoffId: string): Promise<string> {
  if (isTranslatorInviteUrl()) {
    return redeemTranslatorHandoff(handoffId);
  }
  try {
    return await redeemVideoJoinHandoff(handoffId);
  } catch (videoErr) {
    try {
      return await redeemTranslatorHandoff(handoffId);
    } catch (translatorErr) {
      const translatorMessage = translatorErr instanceof Error ? translatorErr.message : '';
      if (translatorMessage && !translatorMessage.toLowerCase().includes('invalid or expired')) {
        throw translatorErr;
      }
      throw videoErr;
    }
  }
}
